/**
 * useNumberingBaseFieldOptions — buduje listę opcji dropdowna „Pole daty numeracji"
 * dla edytora typu dokumentu (`NumberingMaskEditor`).
 *
 * - Pola CORE (bazowe/klasowe) — ze wspólnej stałej `DOCUMENT_CORE_DATE_FIELDS`
 *   (etykiety i18n), zawsze; pierwsze = pole domyślne klasy.
 * - Pola DODATKOWE (ext) typu data/czas — z katalogu kartoteki (`useFieldsForModule`),
 *   TYLKO w scope tenanta (`includeExt`). Panel ITE (typy globalne) korzysta z INNEGO
 *   katalogu dodatkowego (centralny ADM, `system_kartoteka_fields`) — patrz
 *   `buildGlobalNumberingBaseFieldOptions` poniżej, `includeExt` tutaj zawsze `false`.
 *
 * Pola ext o slugu pokrywającym się z core są pomijane (core bierzemy ze stałej,
 * dla spójnych etykiet i18n).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_CORE_DATE_FIELDS, type DocumentClass } from '@saas/shared';
import type { TFunction } from 'i18next';

import {
  DOCUMENTS_TARGET_MODULE_CODE,
  useFieldsForModule,
} from '@/components/process-builder/useFieldsForModule';
import {
  buildNumberingBaseFieldOptions,
  type NumberingBaseFieldOption,
} from '@/lib/record-types/numbering-base-fields';

export type { NumberingBaseFieldOption };

export function useNumberingBaseFieldOptions(
  documentClass: DocumentClass,
  includeExt: boolean,
): NumberingBaseFieldOption[] {
  const { t } = useTranslation();
  // Ext-daty tylko w scope tenanta; bez includeExt query się nie odpala (moduleSlug=null).
  const { data } = useFieldsForModule(
    includeExt ? DOCUMENTS_TARGET_MODULE_CODE : null,
    documentClass,
  );

  return useMemo(
    () =>
      buildNumberingBaseFieldOptions({
        t,
        coreFields: DOCUMENT_CORE_DATE_FIELDS[documentClass],
        extFields: data?.fields,
        skipCatalogCoreFields: false,
      }),
    [documentClass, data, t],
  );
}

/** Pole kartoteki (kształt zwrócony przez `GET /admin/kartoteki/:slug/definition`) — duck-typed,
 * żeby ten plik nie zależał od konkretnego typu `KartotekaBuilderField` z `lib/kartoteki`. */
export interface CentralKartotekaDateFieldLike {
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
}

/**
 * buildGlobalNumberingBaseFieldOptions — wariant CENTRALNY (ADM) dla edytora typu
 * globalnego (panel ITE: `AdminGlobalDocumentTypes`, `DocumentTypeEditorModal`).
 *
 * W przeciwieństwie do `useNumberingBaseFieldOptions` (scope tenanta, fetch z
 * TENANTOWEGO `/system-modules/:slug/fields`) — panel ITE nie ma bazy tenanta do
 * zapytania; jego pola dodatkowe pochodzą z CENTRALNEGO katalogu ADM
 * (`system_kartoteka_fields`), już pobranego przez wołającego przez
 * `useKartotekaDefinition` (`GET /admin/kartoteki/:slug/definition`). Czysta funkcja
 * (bez własnego hooka/fetchu) — wołający decyduje KTÓRY wynik definicji podać:
 * widok klasy (pola wspólne, `typeId` pominięte — zgodne z regułą walidacji POST)
 * albo widok typu (`?typeId=`, pola wspólne ∪ prywatne tego typu — zgodne z PUT).
 *
 * `centralFields` — `undefined` w trakcie ładowania/przy błędzie fetcha → degradacja
 * do samego core (jak `includeExt=false` w wariancie tenantowym).
 */
export function buildGlobalNumberingBaseFieldOptions(
  documentClass: DocumentClass,
  centralFields: readonly CentralKartotekaDateFieldLike[] | undefined,
  t: TFunction,
): NumberingBaseFieldOption[] {
  return buildNumberingBaseFieldOptions({
    t,
    coreFields: DOCUMENT_CORE_DATE_FIELDS[documentClass],
    extFields: centralFields,
    skipCatalogCoreFields: true,
  });
}
